import React, { FC, useMemo, useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Box,
} from '@mui/material';
import { useSetRecoilState } from 'recoil';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import './FriendsFeedbackForm.scss';
import { generalSnackBarAtom, useAuthContext } from '../../../../../data-access';

const friendsFeedbackFormUrl =
  'https://docs.google.com/forms/d/e/1FAIpQLSePaBEkWywzH5yOEdqQAMQvdA3ja16F-ZxJY3HvrqJlw8u1mA/formResponse';
const calendlyMeetingUrl = 'https://calendly.com/hayatowaki/lifestack-onboarding-w-hayato';

enum FormKeys {
  userId = 'entry.739525440',
  useCase = 'entry.700603616', // Dropdown selection
  moreDetails = 'entry.826751502', // Free text feedback
}

const useCaseOptions = [
  { labelKey: 'settings.friends.feedbackUseCases.shareCalendar', value: 'share_calendar' },
  { labelKey: 'settings.friends.feedbackUseCases.shareEnergy', value: 'share_energy' },
  { labelKey: 'settings.friends.feedbackUseCases.findMeetingTime', value: 'find_meeting_time' },
  { labelKey: 'settings.friends.feedbackUseCases.other', value: 'other' },
];

interface FriendsFeedbackFormProps {
  className?: string;
}

export const FriendsFeedbackForm: FC<FriendsFeedbackFormProps> = ({ className }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const setGeneralSnackbar = useSetRecoilState(generalSnackBarAtom);
  const [selectedUseCase, setSelectedUseCase] = useState<string>('');
  const [moreDetails, setMoreDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSend = useMemo(() => {
    return !!selectedUseCase && !!moreDetails;
  }, [selectedUseCase, moreDetails]);

  const showToast = (title: string, message: string) => {
    setGeneralSnackbar(`${title} – ${message}`);
    setTimeout(() => {
      setGeneralSnackbar('');
    }, 6000);
  };

  const handleScheduleMeeting = () => {
    window.open(calendlyMeetingUrl, '_blank');
  };

  const handleUseCaseChange = (event: SelectChangeEvent<string>) => {
    setSelectedUseCase(event.target.value);
  };

  const handleSubmit = async () => {
    if (!canSend || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    const submitParams = new FormData();
    submitParams.append(FormKeys.userId, user.userId);
    submitParams.append(FormKeys.useCase, selectedUseCase);
    submitParams.append(FormKeys.moreDetails, moreDetails);

    try {
      await axios.post(friendsFeedbackFormUrl, submitParams, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Accept: '*/*',
        },
        transformRequest: (data) => {
          return data;
        },
      });
      showToast(
        t('settings.friends.feedbackSubmitted'),
        t('settings.friends.feedbackSubmittedMessage')
      );
      setSelectedUseCase('');
      setMoreDetails('');
    } catch (error) {
      console.error('Error', 'Failed to submit feedback', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={`manage-friends-settings__section friends-feedback-form ${className || ''}`}>
      <Typography variant="body2" className="friends-feedback-form__title">
        {t('settings.nav.shareFeedback')}
      </Typography>

      <FormControl fullWidth size="small" className="friends-feedback-form__dropdown">
        <InputLabel id="use-case-label">{t('settings.friends.selectUseCase')}</InputLabel>
        <Select
          labelId="use-case-label"
          value={selectedUseCase}
          onChange={handleUseCaseChange}
          label={t('settings.friends.selectUseCase')}
        >
          {useCaseOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        className="friends-feedback-form__text-input"
        multiline
        rows={2}
        placeholder={t('settings.friends.moreDetails')}
        value={moreDetails}
        onChange={(e) => setMoreDetails(e.target.value)}
        size="small"
        fullWidth
      />

      <Button
        variant="contained"
        size="small"
        onClick={handleSubmit}
        disabled={!canSend || isSubmitting}
        className="friends-feedback-form__submit-button"
        fullWidth
      >
        {isSubmitting ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          t('settings.common.submit')
        )}
      </Button>

      <Box className="friends-feedback-form__meeting-section">
        <Typography variant="body2" className="friends-feedback-form__meeting-text">
          {t('settings.friends.meetingPrompt')}{' '}
          <a href={calendlyMeetingUrl} target="_blank" rel="noopener noreferrer">
            {t('settings.friends.scheduleMeeting')}
          </a>
        </Typography>
      </Box>
    </Box>
  );
};

export default FriendsFeedbackForm;
